import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/contexts/TranslationContext';

interface AdoptionRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post: any;
  onSuccess: () => void;
}

export default function AdoptionRequestModal({ 
  open, 
  onOpenChange, 
  post, 
  onSuccess 
}: AdoptionRequestModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [meetingAvailability, setMeetingAvailability] = useState('');

  const handleSubmit = async () => {
    if (!user || !post) return;

    if (!message.trim()) {
      toast({
        title: "Messaggio richiesto",
        description: "Scrivi un messaggio di presentazione per il proprietario",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Check if user already made a request for this post
      const { data: existingRequest } = await supabase
        .from('adoption_requests')
        .select('id')
        .eq('adoption_post_id', post.id)
        .eq('requester_id', user.id)
        .maybeSingle();

      if (existingRequest) {
        toast({
          title: "Richiesta già inviata",
          description: "Hai già inviato una richiesta per questo pet",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('adoption_requests')
        .insert([{
          adoption_post_id: post.id,
          requester_id: user.id,
          message: message.trim(),
          meeting_availability: meetingAvailability.trim() || null
        }]);

      if (error) throw error;

      toast({
        title: "Richiesta inviata!",
        description: "Il proprietario riceverà una notifica e potrà rispondere alla tua richiesta",
      });

      onSuccess();
      onOpenChange(false);
      setMessage('');
      setMeetingAvailability('');
    } catch (error) {
      console.error('Error sending adoption request:', error);
      toast({
        title: "Errore",
        description: "Impossibile inviare la richiesta. Riprova più tardi.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Heart className="w-5 h-5 text-primary" />
            <span>{t('adoption.requestTitle')}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Pet Info */}
          <div className="flex items-center space-x-3 p-4 bg-muted/20 rounded-lg">
            <Avatar className="w-12 h-12">
              <AvatarImage src={post.image_url} alt={post.pet_name} />
              <AvatarFallback>{post.pet_name[0]}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-white">{post.pet_name}</h3>
              <p className="text-sm text-muted-foreground">
                {post.species} • {post.location}
              </p>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">{t('adoption.message')} *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('adoption.messagePlaceholder')}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Scrivi un messaggio sincero e dettagliato per aumentare le tue possibilità
            </p>
          </div>

          {/* Meeting Availability */}
          <div className="space-y-2">
            <Label htmlFor="availability">Disponibilità per Incontro (opzionale)</Label>
            <Input
              id="availability"
              value={meetingAvailability}
              onChange={(e) => setMeetingAvailability(e.target.value)}
              placeholder={t('adoption.availabilityPlaceholder')}
            />
          </div>

          {/* Guidelines */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <h4 className="font-medium text-white mb-2">{t('adoption.guidelines')}</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Sii onesto sulla tua esperienza con gli animali</li>
              <li>• Spiega la tua situazione abitativa</li>
              <li>• Mostra interesse genuino per il benessere del pet</li>
              <li>• Rispetta le condizioni di adozione del proprietario</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Annulla
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !message.trim()}
              className="flex-1"
            >
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Invio...' : 'Invia Richiesta'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}